// AM Advertising Home — the page's money render helpers. PURE, and the seam
// where the currency invariant is enforced for this new surface:
//
// ⚠️ A BLENDED MONEY FIGURE EXISTS ONLY VIA THE RATE TABLE. Native mode renders
// per-currency (formatDisplayMoney degrades to the per-currency string);
// display mode converts through the fx snapshot. A raw cross-currency
// `reduce(+)` must remain impossible here — mutation-checked.

import {
  formatCurrencyAmounts,
  formatDisplayMoney,
  formatReportingAmount,
} from '../../lib/reportingMoney';
import {
  convertAmount,
  isNativeDisplay,
  toDisplayCurrency,
} from '../../lib/reporting/currencyConversion';
import type { FxRateSnapshot } from '../../types/fxRates';
import type { CurrencyAmount } from '../../types/reporting';

/** Money for any spend/CPL list — converted in display mode, per-currency native. */
export function homeMoney(
  amounts: CurrencyAmount[] | null | undefined,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): string {
  if (!amounts || amounts.length === 0) return '—';
  return formatDisplayMoney(amounts, display, snapshot);
}

/**
 * Blended CPL = combined spend ÷ combined leads, THROUGH the display-currency
 * machinery:
 * - display mode: the CONVERTED total spend ÷ the all-currency lead count — a
 *   real figure in a real currency. If any amount could not convert, degrade to
 *   the per-currency CPL string rather than divide a partial total (a shortfall
 *   the reader cannot see).
 * - native mode: the per-currency CPLs, never summed.
 */
export function homeBlendedCpl(
  spend: CurrencyAmount[],
  leads: number,
  perCurrencyCpl: CurrencyAmount[] | null,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): string {
  const converted = toDisplayCurrency(spend, display, snapshot);
  if (converted && leads > 0) {
    if (converted.unconverted.length > 0) {
      return perCurrencyCpl ? formatCurrencyAmounts(perCurrencyCpl) : '—';
    }
    return formatReportingAmount(converted.amount / leads, converted.currency);
  }
  return perCurrencyCpl ? formatCurrencyAmounts(perCurrencyCpl) : '—';
}

/**
 * CPL for a row that is IN ONE CURRENCY BY CONSTRUCTION — a creative, which
 * belongs to one ad account and therefore to one ad-account currency.
 *
 * ⚠️⚠️ THIS EXISTS BECAUSE `homeBlendedCpl` RETURNED A DASH FOR EVERY CREATIVE
 * IN NATIVE MODE, and the cause is worth keeping written down: a BLENDED figure
 * has no single-number form natively (that is the whole point of the
 * per-currency rule), so the blended helper falls back to a per-currency CPL
 * string — and the gallery had none to give it, so it printed "—" beside a real
 * lead count. The Meta board hardcodes `'native'`, so it was every card; the AM
 * board only looked right because its display currency is user-chosen.
 *
 * ⚠️ THE FIX IS THE RIGHT TOOL, NOT A FALLBACK BOLTED ON THE WRONG ONE. There
 * is nothing to blend here: one currency in, one figure out. Using a
 * cross-currency helper on a single-currency row was the error.
 *
 * Display mode converts that ONE amount through the rate table and labels it in
 * the display currency; ⚠️ a missing rate degrades to the row's OWN currency
 * rather than to a dash — the native figure is real, correct and labelled with
 * the symbol it is actually in, which is strictly better than withholding it.
 * Native mode renders the row's own currency, which is all it ever was.
 */
export function homeSingleCurrencyCpl(
  spend: number,
  currency: string,
  leads: number,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): string {
  if (!Number.isFinite(spend) || !Number.isFinite(leads) || leads <= 0 || spend <= 0) return '—';
  const cpl = spend / leads;
  if (!isNativeDisplay(display)) {
    const converted = convertAmount(cpl, currency, display, snapshot);
    if (converted !== null) return formatReportingAmount(converted, display);
  }
  return formatReportingAmount(cpl, currency);
}

/**
 * The single scalar a money DELTA can honestly be computed from. Display mode:
 * the fully-converted total (partial conversion → null — no delta over a
 * partial figure). Native mode: only when exactly ONE currency is present —
 * mixed native money has no single figure to take a percentage of (the Google
 * dashboard's delta rule, applied here).
 */
export function moneyScalar(
  amounts: readonly CurrencyAmount[],
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): { value: number; currency: string } | null {
  if (amounts.length === 0) return null;
  const converted = toDisplayCurrency(amounts, display, snapshot);
  if (converted) {
    if (converted.unconverted.length > 0) return null;
    return { value: converted.amount, currency: converted.currency };
  }
  if (amounts.length === 1) return { value: amounts[0]!.amount, currency: amounts[0]!.currency };
  return null;
}

/** Fractional Google conversions display: whole numbers plain, else 1 dp. */
export function formatLeads(leads: number): string {
  if (!Number.isFinite(leads)) return '—';
  const rounded = Math.round(leads * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
